/**
 * Custom queues beside the queue library (RESTORE_PLAN R1.6, from the pre-November
 * QueueManagement "Queue Setup"): type a queue's name and description (and, optionally, its
 * JQL), edit one (Save only when changed) or remove it. Saved on the engagement config with the
 * library's switches; the old screen posted to an address no server answered.
 */
import { useState } from 'react';
import { Edit, ListPlus, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  addCustomQueue,
  customQueueChanged,
  customQueueProblem,
  removeCustomQueue,
  updateCustomQueue,
  type CustomQueue,
  type CustomQueueInput,
} from '../utils/queueLibrary';

interface CustomQueuesPanelProps {
  queues: CustomQueue[];
  /** The library's queue names: a custom queue may not repeat one. */
  libraryNames: string[];
  createdBy?: string;
  /** Saves the whole list; resolves true when the server took it. */
  onSave: (next: CustomQueue[]) => Promise<boolean>;
}

const EMPTY: CustomQueueInput = { name: '', description: '', jql: '' };

export function CustomQueuesPanel({ queues, libraryNames, createdBy, onSave }: CustomQueuesPanelProps) {
  const [draft, setDraft] = useState<CustomQueueInput>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<CustomQueue | null>(null);
  const [editDraft, setEditDraft] = useState<CustomQueueInput>(EMPTY);

  const addProblem = draft.name.trim() ? customQueueProblem(draft, queues, libraryNames) : null;
  const editProblem = editing ? customQueueProblem(editDraft, queues, libraryNames, editing.id) : null;
  const editChanged = editing ? customQueueChanged(editing, editDraft) : false;

  const add = async () => {
    const problem = customQueueProblem(draft, queues, libraryNames);
    if (problem) {
      toast.error(problem);
      return;
    }
    setSaving(true);
    const ok = await onSave(addCustomQueue(queues, draft, createdBy));
    setSaving(false);
    if (ok) {
      toast.success(`Queue "${draft.name.trim()}" added`);
      setDraft(EMPTY);
    }
  };

  const remove = async (queue: CustomQueue) => {
    if (!window.confirm(`Remove the custom queue "${queue.name}"?`)) return;
    setSaving(true);
    const ok = await onSave(removeCustomQueue(queues, queue.id));
    setSaving(false);
    if (ok) toast.success(`Queue "${queue.name}" removed`);
  };

  const saveEdit = async () => {
    if (!editing || editProblem || !editChanged) return;
    setSaving(true);
    const ok = await onSave(updateCustomQueue(queues, editing.id, editDraft));
    setSaving(false);
    if (ok) {
      toast.success(`Queue "${editDraft.name.trim()}" updated`);
      setEditing(null);
    }
  };

  return (
    <Card data-testid="custom-queues">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ListPlus className="w-5 h-5 text-indigo-600" aria-hidden />
          Custom queues
          <Badge variant="outline">{queues.length}</Badge>
        </CardTitle>
        <CardDescription>
          Queues this client needs that the library does not have. Type the name and what it is for; add its JQL when you know it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {queues.length > 0 && (
          <ul className="space-y-2">
            {queues.map((q) => (
              <li key={q.id} className="flex items-start justify-between gap-3 rounded-lg border p-3" data-testid="custom-queue">
                <div className="min-w-0">
                  <p className="font-medium">{q.name}</p>
                  {q.description && <p className="text-sm text-muted-foreground mt-1">{q.description}</p>}
                  {q.jql && <p className="mt-1 font-mono text-xs text-slate-600 break-all">{q.jql}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit queue ${q.name}`}
                    disabled={saving}
                    onClick={() => {
                      setEditing(q);
                      setEditDraft({ name: q.name, description: q.description || '', jql: q.jql || '' });
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" aria-label={`Remove queue ${q.name}`} disabled={saving} onClick={() => remove(q)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-3 rounded-lg border border-dashed p-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="custom-queue-name">Queue Name *</Label>
              <Input
                id="custom-queue-name"
                value={draft.name}
                placeholder="e.g., IT Hardware Support"
                aria-invalid={!!addProblem}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void add();
                  }
                }}
              />
              {addProblem && <p className="text-xs text-destructive">{addProblem}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="custom-queue-description">Description (optional)</Label>
              <Input
                id="custom-queue-description"
                value={draft.description}
                placeholder="What this queue is for and who works it"
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="custom-queue-jql">JQL (optional)</Label>
            <Textarea
              id="custom-queue-jql"
              value={draft.jql}
              rows={2}
              className="font-mono text-xs"
              placeholder='e.g., project = ITSD AND "Request Type" = "Hardware" ORDER BY created ASC'
              onChange={(e) => setDraft((d) => ({ ...d, jql: e.target.value }))}
            />
          </div>
          <Button variant="outline" onClick={() => void add()} disabled={saving || !draft.name.trim() || !!addProblem}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Add Queue
          </Button>
        </div>
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Custom Queue</DialogTitle>
            <DialogDescription>Change the queue's name, description or JQL.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="edit-custom-queue-name">Queue Name *</Label>
              <Input
                id="edit-custom-queue-name"
                value={editDraft.name}
                aria-invalid={!!editProblem}
                onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
              />
              {editProblem && <p className="text-xs text-destructive">{editProblem}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-custom-queue-description">Description (optional)</Label>
              <Textarea
                id="edit-custom-queue-description"
                value={editDraft.description}
                rows={2}
                onChange={(e) => setEditDraft((d) => ({ ...d, description: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-custom-queue-jql">JQL (optional)</Label>
              <Textarea
                id="edit-custom-queue-jql"
                value={editDraft.jql}
                rows={3}
                className="font-mono text-xs"
                onChange={(e) => setEditDraft((d) => ({ ...d, jql: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={() => void saveEdit()} disabled={saving || !!editProblem || !editChanged}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
